import { NgClass } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@cmi/addin-core';
import * as wordControls from '../../../lib/word-content-controls';
import { PropertyFormatForm } from '../../property-format/property-format-form';
import { ProviderTypeIcon } from '../../shared/provider-type-icon/provider-type-icon';
import { ContentProviderProperty } from '../home.models';
import { HomeService } from '../home.service';

@Component({
  selector: 'cmi-search-results',
  imports: [ProviderTypeIcon, TranslatePipe, NgClass, PropertyFormatForm],
  templateUrl: './search-results.html'
})
export class SearchResults {
  private readonly _homeService = inject(HomeService);

  public readonly searchTerm = input.required<string>();
  public readonly tag = input<string | null>(null);
  public readonly closeResults = output<void>();

  protected readonly expandedPropertyKey = signal<string | null>(null);

  protected readonly searchResults = computed(() => {
    const term = this.searchTerm();
    const tagValue = this.tag();

    if (!term || !term.trim()) {
      return [];
    }

    return this._homeService.searchPropertiesGrouped(term, tagValue ?? undefined);
  });

  protected async onAddProperty(
    property: ContentProviderProperty,
    providerName: string
  ): Promise<void> {
    try {
      await wordControls.insertRichTextControl({
        title: `CMI_${providerName}.${property.key}`,
        tag: { extra: [property.key] },
        placeholder: `${property.key}`
      });
    } catch (error) {
      console.error('Error inserting content control:', error);
    }
  }

  protected toggleFormattedInsert(appKey: string, providerKey: string, propertyKey: string): void {
    const key = this._getPropertyKey(appKey, providerKey, propertyKey);
    const currentExpanded = this.expandedPropertyKey();
    if (currentExpanded === key) {
      // Collapse if already expanded
      this.expandedPropertyKey.set(null);
    } else {
      // Expand this property (accordion behavior)
      this.expandedPropertyKey.set(key);
    }
  }

  protected isPropertyExpanded(appKey: string, providerKey: string, propertyKey: string): boolean {
    const key = this._getPropertyKey(appKey, providerKey, propertyKey);
    return this.expandedPropertyKey() === key;
  }

  protected supportsFormatting(typeName: string): boolean {
    return typeName === 'DateTime' || typeName === 'Boolean' || typeName === 'Int32';
  }

  protected async onApplyFormattedProperty(
    property: ContentProviderProperty,
    providerKey: string,
    formatValue: string
  ): Promise<void> {
    await wordControls.insertRichTextControl({
      title: `CMI_${providerKey}.${property.key}:${formatValue}`,
      tag: { extra: [property.key] },
      placeholder: `${property.name}`
    });

    this.expandedPropertyKey.set(null);
  }

  protected onClose(): void {
    this.closeResults.emit();
  }

  private _getPropertyKey(appKey: string, providerKey: string, propertyName: string): string {
    return `${appKey}:${providerKey}:${propertyName}`;
  }
}
